import { ArrowRight, CheckCircle2, PhoneCall } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LeadForm } from "@/components/forms/LeadForm";
import { BeforeAfterSlider } from "@/components/shared/BeforeAfterSlider";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { beforeAfterProjects, losAngelesAreas } from "@/data/siteEnhancements";
import { useSiteContent } from "@/hooks/useSiteContent";

export default function HomePage() {
  const { siteData } = useSiteContent();
  const featuredProjects = siteData.projects.slice(0, 3);

  return (
    <div className="space-y-24 pb-6" data-testid="home-page">
      <section className="px-4 md:px-6" data-testid="home-hero-section">
        <div className="page-section grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
          <div className="space-y-6">
            <p className="uppercase !font-sans !font-semibold !text-sm !tracking-[0.28em] !text-[#EFEEEE] !bg-[#363232]" data-testid="home-hero-eyebrow">solar + roofing experts

            </p>
            <h1 className="text-4xl font-bold sm:text-5xl lg:text-6xl !text-[#000000] !bg-[#2EC12E]" data-testid="home-hero-title">
              {siteData.hero.headline}
            </h1>
            <p className="max-w-2xl text-sm text-muted-foreground md:text-base" data-testid="home-hero-description">
              {siteData.hero.subheadline}
            </p>

            <div className="grid gap-3 sm:grid-cols-2">
              {siteData.stats.map((stat) =>
              <div className="rounded-[1.5rem] border border-primary/10 bg-white/70 p-4" data-testid={`home-stat-${stat.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={stat.label}>
                  <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-3">
              <Button asChild className="rounded-full px-6 py-6 text-sm">
                <a data-testid="home-hero-quote-button" href="#home-quote-form">
                  Get Free Quote
                  <ArrowRight className="h-4 w-4" />
                </a>
              </Button>
              <Button asChild className="rounded-full px-6 py-6 text-sm" variant="outline">
                <a data-testid="home-hero-call-button" href={`tel:${siteData.phone}`}>
                  <PhoneCall className="h-4 w-4" />
                  Call Now: {siteData.phone}
                </a>
              </Button>
            </div>
          </div>

          <div className="image-shell relative aspect-[5/4] overflow-hidden rounded-[2rem]" data-testid="home-hero-image-shell">
            <img alt="Modern home with solar panels" className="h-full w-full object-cover" data-testid="home-hero-image" src={siteData.hero.background_image} />
            <div className="absolute inset-x-4 bottom-4 rounded-[1.5rem] bg-slate-950/82 p-5 text-white backdrop-blur" data-testid="home-hero-overlay-card">
              <p className="text-sm font-semibold uppercase tracking-[0.28em] text-emerald-300" data-testid="home-hero-overlay-label">
                Complete exterior energy support
              </p>
              <p className="mt-3 text-lg font-semibold" data-testid="home-hero-overlay-title">
                From roof condition to solar output, we help property owners plan the full picture.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6" data-testid="home-services-section">
        <div className="page-section space-y-10">
          <SectionHeading description="Lead with the service your property needs most, then explore the supporting solutions that help it perform better over time." eyebrow="Services overview" testIdPrefix="home-services-heading" title="Roofing, solar, and storage built around real property goals." />
          <div className="grid gap-6 lg:grid-cols-3">
            {siteData.services.map((service, index) =>
            <Card className="overflow-hidden rounded-[2rem] border-white/60 bg-white/90 shadow-[0_24px_80px_rgba(16,24,40,0.08)]" data-testid={`home-service-card-${index + 1}`} key={service.id}>
                <img alt={service.title} className="aspect-[4/3] w-full object-cover" data-testid={`home-service-image-${index + 1}`} src={service.image_url} />
                <CardContent className="space-y-4 p-6">
                  <h3 className="text-2xl font-bold text-foreground" data-testid={`home-service-title-${index + 1}`}>
                    {service.title}
                  </h3>
                  <p className="text-sm text-muted-foreground" data-testid={`home-service-description-${index + 1}`}>
                    {service.description}
                  </p>
                  <Button asChild className="rounded-full px-5" variant="outline">
                    <Link data-testid={`home-service-link-${index + 1}`} to={index === 1 ? "/roofing-services" : "/solar-services"}>
                      Explore service
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6" data-testid="home-benefits-section">
        <div className="page-section grid gap-8 rounded-[2.5rem] bg-slate-950 px-6 py-10 text-white lg:grid-cols-[0.95fr_1.05fr] lg:px-10">
          <div className="image-shell aspect-[5/4] overflow-hidden rounded-[2rem]" data-testid="home-benefits-image-shell">
            <img alt="Team working on solar roof" className="h-full w-full object-cover" data-testid="home-benefits-image" src="https://images.pexels.com/photos/14614266/pexels-photo-14614266.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" />
          </div>
          <div className="space-y-6">
            <div className="space-y-4" data-testid="home-benefits-heading-wrapper">
              <p className="text-sm font-semibold uppercase tracking-[0.28em] text-emerald-300" data-testid="home-benefits-heading-eyebrow">
                Why choose us
              </p>
              <h2 className="text-4xl font-bold md:text-5xl" data-testid="home-benefits-heading-title">
                A local team built around trust, quality, and straightforward guidance.
              </h2>
              <p className="max-w-2xl text-sm text-white/70 md:text-base" data-testid="home-benefits-heading-description">
                We focus on clear planning, durable materials, and dependable follow-up so every quote request turns into a confident next step.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {siteData.benefits.map((benefit, index) =>
              <div className="rounded-[1.5rem] border border-white/10 bg-white/5 p-4" data-testid={`home-benefit-item-${index + 1}`} key={benefit}>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-300" />
                    <p className="text-sm text-white/80">{benefit}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6" data-testid="home-projects-section">
        <div className="page-section space-y-10">
          <SectionHeading description="Show homeowners and property managers the kind of workmanship and project range they can expect from your team." eyebrow="Recent projects" testIdPrefix="home-projects-heading" title="Featured work across roofing, solar, and backup-ready energy upgrades." />
          <div className="grid gap-6 lg:grid-cols-3">
            {featuredProjects.map((project) =>
            <Card className="overflow-hidden rounded-[2rem] border-white/60 bg-white/90" data-testid={`home-project-card-${project.id}`} key={project.id}>
                <img alt={project.title} className="aspect-[4/3] w-full object-cover" data-testid={`home-project-image-${project.id}`} src={project.image_url} />
                <CardContent className="space-y-4 p-6">
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary" data-testid={`home-project-category-${project.id}`}>
                      {project.category}
                    </p>
                    <p className="text-xs text-muted-foreground" data-testid={`home-project-location-${project.id}`}>
                      {project.location}
                    </p>
                  </div>
                  <h3 className="text-2xl font-bold text-foreground" data-testid={`home-project-title-${project.id}`}>
                    {project.title}
                  </h3>
                  <p className="text-sm text-muted-foreground" data-testid={`home-project-summary-${project.id}`}>
                    {project.summary}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6" data-testid="home-before-after-section">
        <div className="page-section space-y-10">
          <SectionHeading description="Interactive sliders help visitors visualize the difference between a worn exterior condition and a cleaner, professionally finished result." eyebrow="Before & after project sliders" testIdPrefix="home-before-after-heading" title="Give Los Angeles property owners a clearer picture of what an upgrade can look like." />
          <div className="grid gap-6 xl:grid-cols-3">
            {beforeAfterProjects.map((project) =>
            <BeforeAfterSlider afterImage={project.afterImage} beforeImage={project.beforeImage} description={project.description} key={project.id} testIdPrefix={`home-before-after-${project.id}`} title={project.title} />
            )}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6" data-testid="home-los-angeles-section">
        <div className="page-section grid gap-8 rounded-[2.5rem] bg-white/85 px-6 py-10 shadow-[0_22px_80px_rgba(16,24,40,0.08)] lg:grid-cols-[0.95fr_1.05fr] lg:px-10">
          <div className="image-shell aspect-[5/4] overflow-hidden rounded-[2rem]" data-testid="home-los-angeles-image-shell">
            <img alt="Los Angeles residential neighborhoods" className="h-full w-full object-cover" data-testid="home-los-angeles-image" src="https://images.pexels.com/photos/14946525/pexels-photo-14946525.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" />
          </div>
          <div className="space-y-6">
            <SectionHeading description="Localized trust signals convert better for service businesses, so this section speaks directly to Los Angeles-area homeowners and property managers." eyebrow="Stronger local conversion" testIdPrefix="home-los-angeles-heading" title="Built for Los Angeles roofs, homes, weather patterns, and fast-moving quote decisions." />
            <div className="grid gap-3 sm:grid-cols-2">
              {losAngelesAreas.map((area) =>
              <div className="rounded-full border border-primary/15 bg-primary/5 px-4 py-3 text-sm font-semibold text-foreground" data-testid={`home-los-angeles-area-${area.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={area}>
                  {area}
                </div>
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <Card className="rounded-[1.5rem] border-primary/10 bg-primary/5" data-testid="home-los-angeles-proof-fast-response">
                <CardContent className="space-y-2 p-5">
                  <p className="text-2xl font-bold text-foreground">24 hrs</p>
                  <p className="text-sm text-muted-foreground">Average quote response for local inquiries</p>
                </CardContent>
              </Card>
              <Card className="rounded-[1.5rem] border-primary/10 bg-primary/5" data-testid="home-los-angeles-proof-roof-solar">
                <CardContent className="space-y-2 p-5">
                  <p className="text-2xl font-bold text-foreground">1 team</p>
                  <p className="text-sm text-muted-foreground">Roof + solar coordination under one brand</p>
                </CardContent>
              </Card>
              <Card className="rounded-[1.5rem] border-primary/10 bg-primary/5" data-testid="home-los-angeles-proof-properties-served">
                <CardContent className="space-y-2 p-5">
                  <p className="text-2xl font-bold text-foreground">30+</p>
                  <p className="text-sm text-muted-foreground">Nearby cities and service areas supported</p>
                </CardContent>
              </Card>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button asChild className="rounded-full px-6 py-6 text-sm">
                <a data-testid="home-los-angeles-quote-button" href="#home-quote-form">
                  Get my Los Angeles quote
                </a>
              </Button>
              <Button asChild className="rounded-full px-6 py-6 text-sm" variant="outline">
                <Link data-testid="home-los-angeles-contact-button" to="/contact">
                  Talk with the local team
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6" data-testid="home-testimonials-section">
        <div className="page-section space-y-10">
          <SectionHeading description="Real reviews help this classic business-site layout feel trustworthy and conversion-ready from the first visit." eyebrow="Testimonials" testIdPrefix="home-testimonials-heading" title="What local clients say after working with Monarch." />
          <div className="grid gap-6 lg:grid-cols-3">
            {siteData.testimonials.map((testimonial) =>
            <Card className="rounded-[2rem] border-white/60 bg-white/90 p-2" data-testid={`home-testimonial-card-${testimonial.id}`} key={testimonial.id}>
                <CardContent className="space-y-5 p-4">
                  <img alt={testimonial.name} className="aspect-[4/3] w-full rounded-[1.5rem] object-cover" data-testid={`home-testimonial-image-${testimonial.id}`} src={testimonial.image_url} />
                  <p className="text-lg font-semibold text-foreground" data-testid={`home-testimonial-quote-${testimonial.id}`}>
                    “{testimonial.quote}”
                  </p>
                  <div>
                    <p className="font-semibold text-foreground" data-testid={`home-testimonial-name-${testimonial.id}`}>
                      {testimonial.name}
                    </p>
                    <p className="text-sm text-muted-foreground" data-testid={`home-testimonial-location-${testimonial.id}`}>
                      {testimonial.location}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6" data-testid="home-cta-section" id="home-quote-form">
        <div className="page-section grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
          <div className="space-y-5">
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-primary" data-testid="home-cta-eyebrow">
              Start saving today
            </p>
            <h2 className="text-4xl font-bold text-foreground md:text-5xl" data-testid="home-cta-title">
              Tell us about your property and get a quote built around your goals.
            </h2>
            <p className="max-w-xl text-sm text-muted-foreground md:text-base" data-testid="home-cta-description">
              Use this quick request form to start your solar, roofing, or energy storage conversation with Monarch.
            </p>
          </div>
          <LeadForm buttonLabel="Get Free Quote" description="Share a few details and our team will follow up with the right next step." leadType="quote" pageSource="home-quote" testIdPrefix="home-quote" title="Request your free quote" />
        </div>
      </section>
    </div>);

}